import FilteringControls from "../../../AssetResults/components/ResultsList/Header/FilteringControls";
import { Header } from "../../../AssetResults/components/ResultsList/Header/Header";
import FiltersBar from "features/AssetResults/components/Filters/FiltersBar";
import { useSortController } from "features/AssetResults/components/Sorting/useSortCotroller";
import { useQueryObserver } from "features/AssetResults/hooks/useQueryObserver";
import { type Dates, type Filter, SortType } from "features/AssetResults/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Icon, type DropdownItemProps } from "semantic-ui-react";

import { ContentList } from "../../../AssetResults/components/ContentList/ContentList";
import CustomDropdown from "features/Flows/components/CustomDropdown/CustomDropdown";
import { EmptyState } from "features/Application/components/EmptyState/EmptyState";
import {
  fetchMore,
  initSearch,
  resetFilter,
  updateFilters,
  updateSort,
  reset,
} from "features/Account/redux/history/historySlice";
import {
  getHistoryResults,
  selectFilterOptions,
  selectHistoryResults,
  selectHistoryResultsError,
  selectHistoryResultsReachedEnd,
  selectHistoryResultsStatus,
} from "features/Account/redux/history/historySelectors";

import styles from "./MyHistory.module.scss";

export const MyHistory = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["content", "account"]);

  const { search } = useSelector(getHistoryResults);
  const [initialValues] = useQueryObserver(search);
  const filterOptions = useSelector(selectFilterOptions);

  useEffect(() => {
    dispatch(initSearch({ search: initialValues }));
    return () => {
      dispatch(reset());
    };
  }, [dispatch, initialValues]);

  const onSortChange = useCallback(
    (newValue: any) => {
      dispatch(updateSort({ value: newValue }));
    },
    [dispatch],
  );

  const { showSort, setShowSort, toggleSort, isMobile } = useSortController();

  const dropdownOptions: DropdownItemProps[] = useMemo(
    () => [
      {
        key: "mostRecent",
        text: t("sort.mostRecent", { ns: "content" }),
        value: SortType.Relevant,
      },
    ],
    [t],
  );

  const [filtersCount, setFiltersCount] = useState(0);
  useEffect(() => {
    setFiltersCount(
      Object.values(search.appliedFilters)
        .flat()
        .filter(x => !!x).length,
    );
  }, [search.appliedFilters, setFiltersCount]);
  const [show, setShow] = useState(false);

  const onUpdate = useCallback(
    (filter: Filter, newValue: string[] | Dates) => {
      dispatch(
        updateFilters({
          filter,
          newValue,
        }),
      );
    },
    [dispatch],
  );

  const onReset = useCallback(
    (filter: Filter) => {
      dispatch(resetFilter(filter));
    },
    [dispatch],
  );

  const isSortIcon = (target: HTMLInputElement) => target.className.includes("sort amount down icon");

  const handleCloseDropdown = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLInputElement;
      if (!isSortIcon(target)) setShowSort(false);
    },
    [setShowSort],
  );

  return (
    <div className={styles.root}>
      <div className={styles.flexGrid}>
        <Header
          controls={
            <FilteringControls
              filtersCount={filtersCount}
              setShowFilters={setShow}
              setSortOpen={setShowSort}
              showFilters={show}
              sortOpen={showSort}
            />
          }
        />
        <div className={styles.mainContent}>
          <div className={styles.filterBarContainer}>
            <FiltersBar
              filterValues={search.appliedFilters}
              filters={filterOptions}
              update={onUpdate}
              reset={onReset}
              show={show}
              setShow={setShow}
            />
          </div>
          <div className={styles.flowList}>
            <ContentList
              closeDropdown={handleCloseDropdown}
              hideTitle
              dropdown={
                <>
                  <CustomDropdown
                    options={dropdownOptions}
                    isVisible={showSort}
                    toggleVisible={toggleSort}
                    defaultValue={initialValues.sortType}
                    onChange={onSortChange}
                    isMobile={isMobile}
                  />
                  <div>
                    <p className={styles.waitTime}>{t("myHistory.waitTime", { ns: "account" })}</p>
                  </div>
                </>
              }
              selectResult={selectHistoryResults}
              selectStatus={selectHistoryResultsStatus}
              selectError={selectHistoryResultsError}
              selectReachedEnd={selectHistoryResultsReachedEnd}
              fetchMore={fetchMore}
              emptyStatePage={
                <EmptyState
                  icon={
                    <span className={styles.iconWrapper}>
                      <Icon name="clock" size="large" />
                    </span>
                  }
                  title={t("myHistory.noAvailableHistory", { ns: "account" })}
                  description={
                    <p className={styles.emptyDescription}>{t("myHistory.noContentHistory", { ns: "account" })}</p>
                  }
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
